import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── Shared style tokens ─────────────────────────────────────────────────────
const C = {
  navy:    '#1e3a5f',
  midNavy: '#2d5490',
  accent:  '#4f46e5',
  green:   '#10b981',
  gold:    '#d97706',
  red:     '#ef4444',
  orange:  '#f97316',
  bg:      '#0f172a',
  card:    '#1e293b',
  border:  'rgba(255,255,255,0.08)',
  text:    '#e2e8f0',
  muted:   '#94a3b8',
};

const TABS = [
  { id: 'overview',    label: '📖 Overview'  },
  { id: 'earning',     label: '⚡ Earning'   },
  { id: 'levels',      label: '🏆 Levels'    },
  { id: 'maintenance', label: '🛡️ Maintain' },
  { id: 'decay',       label: '⚠️ Decay'     },
  { id: 'protection',  label: '🔒 Safe'      },
  { id: 'streaks',     label: '🔥 Streaks'   },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionTitle({ children, color = C.accent }) {
  return (
    <h2 style={{
      fontSize: '1.1rem', fontWeight: 700, color, textTransform: 'uppercase',
      letterSpacing: '0.08em', margin: '0 0 16px 0',
      borderBottom: `2px solid ${color}`, paddingBottom: '8px',
    }}>
      {children}
    </h2>
  );
}

function Card({ children, style = {} }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: '12px', padding: '20px', marginBottom: '16px', ...style
    }}>
      {children}
    </div>
  );
}

function RuleBlock({ icon, text, color = C.gold }) {
  return (
    <div style={{
      background: `${color}18`, border: `1px solid ${color}40`,
      borderLeft: `4px solid ${color}`, borderRadius: '8px',
      padding: '12px 16px', marginBottom: '12px',
      display: 'flex', alignItems: 'flex-start', gap: '10px',
    }}>
      <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{icon}</span>
      <span style={{ color: C.text, fontSize: '0.9rem', lineHeight: 1.5 }}>{text}</span>
    </div>
  );
}

function DataTable({ headers, rows, colWidths }) {
  return (
    <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{
                background: C.navy, color: '#a5c8ff', padding: '10px 12px',
                textAlign: i === 0 ? 'left' : 'center', fontWeight: 700,
                border: `1px solid ${C.border}`, width: colWidths?.[i],
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.03)' }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{
                  padding: '9px 12px', color: cell.color || C.text,
                  fontWeight: cell.bold ? 700 : 400,
                  textAlign: ci === 0 ? 'left' : 'center',
                  border: `1px solid ${C.border}`,
                  fontSize: cell.size || '0.85rem',
                }}>
                  {cell.text || cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Tab content ──────────────────────────────────────────────────────────────

function OverviewTab() {
  return (
    <div>
      <Card>
        <SectionTitle>What Are Transformation Points?</SectionTitle>
        <p style={{ color: C.text, lineHeight: 1.7, marginBottom: '16px' }}>
          Transformation Points are the universal currency of your FHO journey. Every interaction
          with the platform — sessions with AI coaches, logging practices, maintaining streaks —
          earns you points. Your total points determine your <strong style={{ color: C.accent }}>Consciousness Level</strong>,
          your <strong style={{ color: C.accent }}>Current Stage</strong>, and what capabilities unlock as you grow.
        </p>
        <RuleBlock icon="⚡" text="Core Principle: To maintain your level, you must continue to do the work. Points decay when you disengage — but safety nets exist to protect your progress." color={C.gold} />
      </Card>

      <Card>
        <SectionTitle color={C.green}>The 4 Journey Phases</SectionTitle>
        {[
          { phase: 'Installation', levels: '1–2', desc: 'Foundation building. You\'re learning the platform and establishing habits.' },
          { phase: 'Operation',    levels: '3–5', desc: 'Active engagement. All three MindStreams unlocked. Consistent practice begins.' },
          { phase: 'Maintenance',  levels: '6–9', desc: 'Deepening practice. Transforming patterns and developing leadership capacity.' },
          { phase: 'Upgrade',      levels: '10–12', desc: 'Mastery phase. You\'ve achieved Free Happy One status and are embodying sustained transformation.' },
        ].map((p, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: '16px',
            padding: '12px 0', borderBottom: i < 3 ? `1px solid ${C.border}` : 'none',
          }}>
            <div style={{
              background: C.accent, color: '#fff', borderRadius: '8px',
              padding: '4px 10px', fontSize: '0.75rem', fontWeight: 700,
              flexShrink: 0, minWidth: '100px', textAlign: 'center', marginTop: '2px',
            }}>{p.phase}</div>
            <div>
              <span style={{ color: C.muted, fontSize: '0.8rem' }}>Levels {p.levels}  </span>
              <span style={{ color: C.text, fontSize: '0.88rem' }}>{p.desc}</span>
            </div>
          </div>
        ))}
      </Card>

      <Card>
        <SectionTitle color={C.gold}>Balance Score</SectionTitle>
        <p style={{ color: C.text, fontSize: '0.88rem', lineHeight: 1.6, marginBottom: '14px' }}>
          The Balance Score measures how evenly your three MindStreams — Power, Flow, and Clarity —
          are developing. It is separate from Consciousness Level and does not affect your stage.
          It is a diagnostic tool to guide where to focus your energy.
        </p>
        {[
          { range: '70–100%', label: 'Well Balanced', color: C.green, desc: 'MindStreams developing evenly. Optimal growth.' },
          { range: '40–69%',  label: 'Moderate',      color: C.gold,  desc: 'Some imbalance. One stream may be lagging.' },
          { range: '0–39%',   label: 'Focus Needed',  color: C.red,   desc: 'Significant imbalance. Direct attention to the weakest stream.' },
        ].map((b, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            padding: '10px 0', borderBottom: i < 2 ? `1px solid ${C.border}` : 'none',
          }}>
            <div style={{ fontWeight: 700, color: b.color, minWidth: '70px', fontSize: '0.9rem' }}>{b.range}</div>
            <div style={{
              background: `${b.color}20`, color: b.color, borderRadius: '6px',
              padding: '2px 10px', fontSize: '0.78rem', fontWeight: 700, minWidth: '100px', textAlign: 'center',
            }}>{b.label}</div>
            <div style={{ color: C.muted, fontSize: '0.85rem' }}>{b.desc}</div>
          </div>
        ))}
      </Card>
    </div>
  );
}

function EarningTab() {
  return (
    <div>
      <Card>
        <SectionTitle>Practice Session Points (Primary Source)</SectionTitle>
        <p style={{ color: C.muted, fontSize: '0.85rem', marginBottom: '14px' }}>
          Every practice session calculates points using this formula:
        </p>
        <div style={{
          background: 'rgba(79,70,229,0.12)', border: `1px solid ${C.accent}40`,
          borderRadius: '10px', padding: '16px', marginBottom: '16px', fontFamily: 'monospace',
          fontSize: '0.88rem', color: C.text, lineHeight: 2,
        }}>
          <div><span style={{ color: C.muted }}>Base Points</span>   = Duration (minutes)</div>
          <div><span style={{ color: C.muted }}>× Quality Multiplier</span>  = Quality Rating ÷ 10</div>
          <div><span style={{ color: C.muted }}>× Streak Bonus</span>  = 1 + (Streak Days × 0.05)</div>
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '8px', marginTop: '4px' }}>
            <strong style={{ color: C.accent }}>Final Points</strong>  = round(Base × Quality × Streak)
          </div>
        </div>
        <RuleBlock icon="📌" text="Example: 30-min session, quality 8/10, on a 4-day streak → 30 × 0.8 × 1.20 = 28.8 → 29 points." color={C.accent} />
      </Card>

      <Card>
        <SectionTitle color={C.green}>All Earning Categories</SectionTitle>
        <DataTable
          headers={['Activity', 'Points']}
          rows={[
            ['Daily login bonus', '+10 pts'],
            ['Streak day maintained', '+5 pts/day'],
            ['Weekly consistency (5+ days/week)', '+25 pts'],
            ['Practice entry logged', '+2 pts'],
            ['Insight recorded with practice', '+5 pts'],
            ['Full reflection completed', '+10 pts'],
            ['Session completed (any coach)', '+5 pts'],
            ['Session completed with 5-star rating', '+10 pts'],
            ['Session using 2+ coaches', '+3 pts bonus'],
            [{ text: 'Any BFA score reaches ≥ 80', color: C.gold }, { text: '+50 pts', color: C.gold, bold: true }],
            [{ text: 'All BFA scores reach ≥ 70', color: C.gold }, { text: '+100 pts', color: C.gold, bold: true }],
            [{ text: 'Any BFA score improves by 10 points', color: C.gold }, { text: '+25 pts', color: C.gold, bold: true }],
          ]}
        />
      </Card>
    </div>
  );
}

function LevelsTab() {
  const levels = [
    { n:1,  name:'SEEKING',       range:'0 – 120',       phase:'Installation', badge:'Truth Seeker',         unlock:'Starting level — explore the platform' },
    { n:2,  name:'UNDERSTANDING', range:'121 – 300',     phase:'Installation', badge:'Power Seeker',          unlock:'Power MindStream + Imagination & Reasoning' },
    { n:3,  name:'EMBRACING',     range:'301 – 600',     phase:'Operation',    badge:'Love-Spectrum Activator',unlock:'Flow MindStream + Intuition & Perception' },
    { n:4,  name:'DEMONSTRATING', range:'601 – 1,000',   phase:'Operation',    badge:'Clarity Bringer',       unlock:'Clarity MindStream + All 36 Dimensions' },
    { n:5,  name:'SIMINATING',    range:'1,001 – 1,500', phase:'Operation',    badge:'FHO Practitioner',      unlock:'Simination Score + FAME tracking + All tools' },
    { n:6,  name:'TRANSFORMING',  range:'1,501 – 2,000', phase:'Maintenance',  badge:'Energy Transformer',    unlock:'THAT Energy System + Freedom Score' },
    { n:7,  name:'ASCENDING',     range:'2,001 – 2,700', phase:'Maintenance',  badge:'Swamp Climber',         unlock:'3D Swamp Escape metrics' },
    { n:8,  name:'TRANSCENDING',  range:'2,701 – 3,500', phase:'Maintenance',  badge:'3D Swamp Escapee',      unlock:'Full Transcendence' },
    { n:9,  name:'ILLUMINATING',  range:'3,501 – 4,500', phase:'Maintenance',  badge:'I-WE Harmonizer',       unlock:'FHOlific Leadership + Teaching capabilities' },
    { n:10, name:'EMBODYING',     range:'4,501 – 5,800', phase:'Upgrade',      badge:'Free Happy One',        unlock:'Free Happy One status + Perfect Simination' },
    { n:11, name:'RADIATING',     range:'5,801 – 7,500', phase:'Upgrade',      badge:'Spirit Made Physical',  unlock:'Spirit Made Physical + Legacy creation' },
    { n:12, name:'MASTERING',     range:'7,501+',         phase:'Upgrade',      badge:'Divine Stasis Master',  unlock:'Divine Stasis Master + Co-creator status' },
  ];

  const phaseColor = { Installation: C.accent, Operation: C.green, Maintenance: C.gold, Upgrade: '#a855f7' };

  return (
    <div>
      <Card>
        <SectionTitle>The 12 Consciousness Levels</SectionTitle>
        <p style={{ color: C.muted, fontSize: '0.85rem', marginBottom: '16px' }}>
          Level is determined automatically by total Transformation Points. Users cannot manually adjust their level.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {levels.map(l => (
            <div key={l.n} style={{
              background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`,
              borderLeft: `3px solid ${phaseColor[l.phase]}`,
              borderRadius: '8px', padding: '12px 14px',
              display: 'flex', alignItems: 'flex-start', gap: '12px',
            }}>
              <div style={{
                background: phaseColor[l.phase], color: '#fff', borderRadius: '50%',
                width: '28px', height: '28px', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700, flexShrink: 0,
              }}>{l.n}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                  <span style={{ fontWeight: 700, color: C.text, fontSize: '0.9rem' }}>{l.name}</span>
                  <span style={{ color: C.muted, fontSize: '0.78rem' }}>{l.range} pts</span>
                  <span style={{
                    background: `${phaseColor[l.phase]}20`, color: phaseColor[l.phase],
                    borderRadius: '4px', padding: '1px 7px', fontSize: '0.72rem', fontWeight: 600,
                  }}>{l.phase}</span>
                </div>
                <div style={{ color: '#d4a820', fontSize: '0.78rem', marginBottom: '2px' }}>
                  🏅 {l.badge}
                </div>
                <div style={{ color: C.muted, fontSize: '0.8rem' }}>{l.unlock}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function MaintenanceTab() {
  return (
    <div>
      <Card>
        <SectionTitle>Activity Requirements by Level Tier</SectionTitle>
        <RuleBlock icon="⚡" text="You don't reach Mastering and coast. True transformation requires continuous engagement." color={C.gold} />
        <DataTable
          headers={['Level Tier', 'Stage Names', 'Must Engage Every']}
          rows={[
            [
              { text: 'Levels 1–4', color: C.green, bold: true },
              'Seeking → Demonstrating',
              { text: '7 days', color: C.green, bold: true },
            ],
            [
              { text: 'Levels 5–8', color: C.gold, bold: true },
              'Siminating → Transcending',
              { text: '5 days', color: C.gold, bold: true },
            ],
            [
              { text: 'Levels 9–12', color: C.red, bold: true },
              'Illuminating → Mastering',
              { text: '3 days', color: C.red, bold: true },
            ],
          ]}
        />
        <p style={{ color: C.muted, fontSize: '0.82rem', marginTop: '8px' }}>
          The higher your level, the more frequently you must engage to maintain it. This reflects
          the reality that mastery requires ongoing, active practice.
        </p>
      </Card>

      <Card>
        <SectionTitle color={C.green}>Grace Period</SectionTitle>
        <p style={{ color: C.text, fontSize: '0.88rem', lineHeight: 1.6, marginBottom: '14px' }}>
          After your maintenance window expires, you have a <strong style={{ color: C.green }}>5-day Grace Period</strong> before
          any points are lost.
        </p>
        <DataTable
          headers={['Status', 'Days Past Maintenance Window', 'Consequence']}
          rows={[
            [
              { text: 'Safe', color: C.green, bold: true },
              'Days 1–5',
              { text: 'No penalty. No points lost.', color: C.green },
            ],
            [
              { text: 'At Risk', color: C.gold, bold: true },
              'Day 5 (final grace day)',
              { text: 'Warning notification sent.', color: C.gold },
            ],
            [
              { text: 'Decay Active', color: C.red, bold: true },
              'Day 6+',
              { text: 'Point decay begins.', color: C.red },
            ],
          ]}
        />
        <RuleBlock icon="📌" text="The grace period resets every time you log a qualifying session." color={C.accent} />
      </Card>
    </div>
  );
}

function DecayTab() {
  return (
    <div>
      <Card>
        <SectionTitle color={C.orange}>Point Decay Rules</SectionTitle>
        <p style={{ color: C.text, fontSize: '0.88rem', lineHeight: 1.6, marginBottom: '14px' }}>
          If you exceed both your maintenance window and the 5-day grace period without a session,
          the system applies <strong style={{ color: C.orange }}>daily point decay</strong> automatically each night at midnight.
        </p>
        <RuleBlock icon="⚠️" text="Decay erodes points, not levels directly. Level drops are a consequence of point loss — not an instant penalty." color={C.orange} />
        <DataTable
          headers={['Days Inactive', 'Daily Decay Rate', 'Example Loss (3,500 pts)', 'Severity']}
          rows={[
            [
              { text: '1–5 days',    color: C.green  },
              { text: '0%',          color: C.green, bold: true },
              { text: 'None',        color: C.green  },
              { text: 'Grace Period',color: C.green, bold: true },
            ],
            [
              { text: '6–10 days',   color: C.gold   },
              { text: '5% / day',    color: C.gold,  bold: true },
              { text: '175 pts/day', color: C.gold   },
              { text: 'Mild',        color: C.gold,  bold: true },
            ],
            [
              { text: '11–20 days',  color: C.orange },
              { text: '10% / day',   color: C.orange, bold: true },
              { text: '350 pts/day', color: C.orange },
              { text: 'Moderate',    color: C.orange, bold: true },
            ],
            [
              { text: '21+ days',    color: C.red    },
              { text: '15% / day',   color: C.red,   bold: true },
              { text: '525 pts/day', color: C.red    },
              { text: 'Severe',      color: C.red,   bold: true },
            ],
          ]}
        />
        <RuleBlock icon="🛡️" text="A hard minimum floor prevents points from reaching zero. You can never be fully reset through decay alone." color={C.green} />
      </Card>

      <Card>
        <SectionTitle color={C.gold}>Notification Sequence</SectionTitle>
        {[
          { day: 'Day 3', msg: '"We miss you — your journey is waiting."', color: C.green, desc: 'Gentle re-engagement nudge.' },
          { day: 'Day 5', msg: '"Your journey is at risk — return today."', color: C.gold, desc: 'Final grace day. Last chance before decay.' },
          { day: 'Day 6', msg: '"Points are now decaying — log a session to stop it."', color: C.red, desc: 'Decay has started. Immediate action required.' },
        ].map((n, i) => (
          <div key={i} style={{
            display: 'flex', gap: '12px', alignItems: 'flex-start',
            padding: '12px 0', borderBottom: i < 2 ? `1px solid ${C.border}` : 'none',
          }}>
            <div style={{
              background: `${n.color}20`, color: n.color, borderRadius: '6px',
              padding: '3px 10px', fontSize: '0.78rem', fontWeight: 700, flexShrink: 0,
            }}>{n.day}</div>
            <div>
              <div style={{ color: C.text, fontSize: '0.85rem', marginBottom: '3px' }}>{n.msg}</div>
              <div style={{ color: C.muted, fontSize: '0.78rem' }}>{n.desc}</div>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

function ProtectionTab() {
  return (
    <div>
      <Card>
        <SectionTitle color={C.green}>🧊 Freeze Tokens</SectionTitle>
        {[
          { label: 'How Earned',   value: '1 token awarded automatically at every 30-day streak milestone.' },
          { label: 'Maximum Held', value: 'Up to 3 Freeze Tokens stored at any time.' },
          { label: 'How Used',     value: 'Automatically consumed (1 token/day) when decay would otherwise trigger.' },
          { label: 'Effect',       value: 'Each token absorbs exactly 1 full day of decay — points fully protected.' },
          { label: 'Cost',         value: 'Free. Earned through engagement. Not a paid feature.' },
        ].map((r, i) => (
          <div key={i} style={{
            display: 'flex', gap: '12px', padding: '10px 0',
            borderBottom: i < 4 ? `1px solid ${C.border}` : 'none',
          }}>
            <div style={{ color: C.green, fontWeight: 700, fontSize: '0.82rem', minWidth: '110px', flexShrink: 0 }}>{r.label}</div>
            <div style={{ color: C.text, fontSize: '0.85rem', lineHeight: 1.5 }}>{r.value}</div>
          </div>
        ))}
      </Card>

      <Card>
        <SectionTitle color={C.accent}>⏪ Restoration Window</SectionTitle>
        {[
          { label: 'Window',   value: '48 hours from the moment decay first begins.' },
          { label: 'Action',   value: 'Complete any 2 qualifying sessions within the 48-hour window.' },
          { label: 'Effect',   value: 'All points lost during the current decay episode are fully restored.' },
          { label: 'Limit',    value: 'Restoration applies to the current decay episode only.' },
        ].map((r, i) => (
          <div key={i} style={{
            display: 'flex', gap: '12px', padding: '10px 0',
            borderBottom: i < 3 ? `1px solid ${C.border}` : 'none',
          }}>
            <div style={{ color: C.accent, fontWeight: 700, fontSize: '0.82rem', minWidth: '70px', flexShrink: 0 }}>{r.label}</div>
            <div style={{ color: C.text, fontSize: '0.85rem', lineHeight: 1.5 }}>{r.value}</div>
          </div>
        ))}
      </Card>

      <Card>
        <SectionTitle color={C.gold}>🏁 Level Floor</SectionTitle>
        <RuleBlock icon="🛡️" text="You can never drop below Level 1 (Seeking) through decay. Your journey is never fully erased." color={C.green} />
        <RuleBlock icon="🛡️" text="A hard point minimum prevents reaching zero, regardless of how long you are inactive." color={C.green} />
      </Card>
    </div>
  );
}

function StreaksTab() {
  return (
    <div>
      <Card>
        <SectionTitle color={C.orange}>Streak Rules</SectionTitle>
        <DataTable
          headers={['Rule', 'Detail']}
          rows={[
            ['How Built',             'Log at least 1 session per calendar day to extend streak by 1.'],
            ['Breaking a Streak',     'Missing a full calendar day with no session resets streak to zero.'],
            ['Best Streak',           'Your all-time highest streak is preserved and displayed separately.'],
            [
              { text: 'Streak Point Bonus', color: C.gold, bold: true },
              { text: '+5% bonus per streak day applied to all session points. A 10-day streak = ×1.50 multiplier.', color: C.gold },
            ],
            [
              { text: 'Freeze Token Award', color: C.green, bold: true },
              { text: 'Every 30-day streak milestone automatically awards 1 Freeze Token (max 3 stored).', color: C.green },
            ],
            ['Days of Transformation', 'Cumulative days of active engagement. Never reset by missed days. Shown in the hero card.'],
          ]}
        />
      </Card>

      <Card>
        <SectionTitle>Streak Bonus Calculator</SectionTitle>
        <p style={{ color: C.muted, fontSize: '0.85rem', marginBottom: '14px' }}>
          Streak multiplier = 1 + (streak days × 0.05)
        </p>
        <DataTable
          headers={['Streak Length', 'Multiplier', 'Bonus on 30-min / Quality 8 Session']}
          rows={[
            ['0 days (no streak)', '×1.00', '24 pts'],
            ['4 days',             '×1.20', '29 pts'],
            ['10 days',            '×1.50', '36 pts'],
            ['19 days (current)',  '×1.95', '47 pts'],
            ['30 days',            '×2.50', '60 pts  +  🧊 Freeze Token earned'],
          ]}
        />
        <RuleBlock icon="🔥" text="The longer your streak, the more every session is worth. A 30-day streak is worth 2.5× a first-day session." color={C.orange} />
      </Card>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const JourneyRulesPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const tabContent = {
    overview:    <OverviewTab />,
    earning:     <EarningTab />,
    levels:      <LevelsTab />,
    maintenance: <MaintenanceTab />,
    decay:       <DecayTab />,
    protection:  <ProtectionTab />,
    streaks:     <StreaksTab />,
  };

  return (
    <div style={{
      minHeight: '100vh', background: C.bg, color: C.text,
      fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${C.navy} 0%, #1a2a4a 100%)`,
        borderBottom: `1px solid ${C.border}`, padding: '0 24px',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{
          maxWidth: '860px', margin: '0 auto', padding: '16px 0',
          display: 'flex', alignItems: 'center', gap: '16px',
        }}>
          <button
            onClick={() => navigate('/journey')}
            style={{
              background: 'rgba(255,255,255,0.08)', border: `1px solid ${C.border}`,
              color: C.text, borderRadius: '8px', padding: '7px 14px',
              cursor: 'pointer', fontSize: '0.85rem', display: 'flex',
              alignItems: 'center', gap: '6px', flexShrink: 0,
            }}
          >
            ← Journey
          </button>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#fff' }}>
              Points &amp; Level Rules
            </div>
            <div style={{ fontSize: '0.75rem', color: C.muted }}>
              How the FHO progression system works
            </div>
          </div>
        </div>

        {/* Tab bar — 2-row grid for mobile */}
        <div style={{
          maxWidth: '860px', margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '3px',
          padding: '8px 12px',
        }}>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                background: activeTab === t.id ? C.accent : 'rgba(255,255,255,0.05)',
                border: activeTab === t.id ? 'none' : '1px solid rgba(255,255,255,0.08)',
                color: activeTab === t.id ? '#fff' : C.muted,
                padding: '7px 4px', cursor: 'pointer', fontSize: '0.72rem',
                fontWeight: activeTab === t.id ? 700 : 400,
                borderRadius: '6px', whiteSpace: 'nowrap',
                textAlign: 'center', transition: 'all 0.15s',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '24px 16px' }}>
        {tabContent[activeTab]}

        {/* Footer */}
        <div style={{
          textAlign: 'center', padding: '32px 0 16px',
          borderTop: `1px solid ${C.border}`, marginTop: '16px',
        }}>
          <div style={{ color: C.muted, fontSize: '0.78rem', lineHeight: 1.6 }}>
            FHO Platform  ·  Points &amp; Level Maintenance Rules  ·  Version 1.0
          </div>
          <div style={{ color: C.muted, fontSize: '0.75rem', marginTop: '4px' }}>
            MindStreams Technology / Thornbee LLC  ·  freehappyone.com
          </div>
          <button
            onClick={() => navigate('/journey')}
            style={{
              marginTop: '20px', background: C.accent, border: 'none',
              color: '#fff', borderRadius: '8px', padding: '10px 24px',
              cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600,
            }}
          >
            ← Back to My Journey
          </button>
        </div>
      </div>
    </div>
  );
};

export default JourneyRulesPage;
